import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg, fileUrl } from "@/lib/api";
import ElmMark from "@/components/ElmMark";
import { applyBranding } from "@/lib/branding";
import { SHORE } from "@/lib/images";
import { ErrorState, Loading } from "@/components/States";
import ShareBar from "@/components/ShareBar";

function Slider({ before, after, name }) {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative aspect-[4/3] select-none overflow-hidden rounded-2xl" onPointerMove={(e) => { if (e.buttons) { const r = e.currentTarget.getBoundingClientRect(); setPos(Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100))); } }} data-testid="lookbook-slider">
      <img src={fileUrl(after)} alt={name} draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}><img src={fileUrl(before)} alt="" draggable={false} className="h-full w-full object-cover" style={{ width: `${10000 / Math.max(pos, 1)}%`, maxWidth: "none" }} /></div>
      <div className="absolute inset-y-0 w-0.5 bg-white shadow" style={{ left: `${pos}%` }}><span className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-stone-900/70" /></div>
      <span className="absolute left-3 top-3 rounded bg-black/55 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white">Before</span>
      <span className="absolute right-3 top-3 rounded bg-black/55 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white">After</span>
    </div>
  );
}

function Project({ p, i, shareBase, apiBase }) {
  return (
    <article id={`project-${p.id}`} className="rise scroll-mt-8 space-y-8" style={{ animationDelay: `${i * 80}ms` }} data-testid={`lookbook-project-${p.id}`}>
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-end">
        <img src={p.hero_url ? fileUrl(p.hero_url) : SHORE} alt="" className="aspect-[16/10] w-full rounded-3xl object-cover" />
        <div className="lg:pb-6">
          <p className="eyebrow">{p.year || "Completed"} · {p.rooms.length} room{p.rooms.length === 1 ? "" : "s"}</p>
          <h2 className="mt-3 font-serif text-4xl leading-tight sm:text-5xl">{p.name}</h2>
          {p.description && <p className="mt-4 text-muted-foreground">{p.description}</p>}
          {p.palettes[0] && <div className="mt-6 flex h-10 overflow-hidden rounded-full">{p.palettes[0].colors.map((c, j) => <div key={`${c.hex}-${j}`} className="flex-1" style={{ background: c.hex }} title={c.name} />)}</div>}
          <div className="mt-6"><ShareBar compact url={`${shareBase}?project=${p.id}`} title={p.name} text={p.description} image={`${apiBase}/api/og/project/${p.id}.png`} /></div>
        </div>
      </div>
      {p.rooms.filter((r) => r.story || r.narration).length > 0 && (
        <div className="grid gap-6 md:grid-cols-2">
          {p.rooms.filter((r) => r.story || r.narration).map((r) => (
            <div key={r.id} className="rounded-2xl border bg-card p-6">
              <p className="eyebrow">{r.name}</p>
              {r.story && <p className="mt-2 font-serif text-xl">{r.story}</p>}
              {r.narration && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{r.narration}</p>}
            </div>
          ))}
        </div>
      )}
      {p.pieces.length > 0 && (
        <div>
          <p className="eyebrow">Pieces given a second life</p>
          <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {p.pieces.map((pc) => <div key={pc.id}><Slider before={pc.before_url} after={pc.after_url} name={pc.name} /><p className="mt-3 font-medium">{pc.name}</p>{pc.finish && <p className="text-sm text-muted-foreground">{pc.finish}</p>}</div>)}
          </div>
        </div>
      )}
      {p.photos.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{p.photos.map((ph, j) => <img key={ph.id || ph.url} src={fileUrl(ph.url)} alt={ph.caption || ""} className={`w-full rounded-xl object-cover ${j === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-square"}`} />)}</div>
      )}
    </article>
  );
}

export default function Lookbook() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get(`/public/lookbook?slug=${slug}`).then((r) => { setData(r.data); applyBranding(r.data.workspace.branding); }).catch((e) => setError(errMsg(e))); }, [slug]);
  if (error) return <div className="p-10"><ErrorState message={error} /></div>;
  if (!data) return <div className="p-10"><Loading rows={4} /></div>;
  const ws = data.workspace;
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const shareBase = `${apiBase}/api/share/lookbook/${slug}`;
  return (
    <div className="min-h-screen" data-testid="lookbook-page">
      <header className="flex items-center justify-between gap-4 px-6 py-6 sm:px-12">
        {ws.branding.logo_url ? <img src={fileUrl(ws.branding.logo_url)} alt={ws.name} className="h-10 object-contain" /> : <p className="font-serif text-3xl text-primary" data-testid="lookbook-brand">{ws.name}</p>}
        <ShareBar url={shareBase} title={`${ws.name} · Lookbook`} text={ws.branding.tagline} image={`${apiBase}/api/og/lookbook/${slug}.png`} />
      </header>
      <section className="px-6 pb-16 pt-8 sm:px-12 sm:pt-16">
        <p className="eyebrow">Lookbook</p>
        <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Rooms we've shaped and pieces we've brought back to life.</h1>
        <div className="mt-8 flex flex-wrap gap-8 text-sm" data-testid="lookbook-stats">
          {[["projects", "Projects"], ["rooms", "Rooms"], ["pieces", "Pieces refinished"]].map(([k, l]) => <div key={k}><p className="stat text-4xl">{data.stats[k]}</p><p className="text-muted-foreground">{l}</p></div>)}
        </div>
      </section>
      <main className="space-y-24 px-6 pb-24 sm:px-12">
        {data.projects.length === 0 ? <p className="text-muted-foreground" data-testid="lookbook-empty">The first completed project will appear here.</p> : data.projects.map((p, i) => <Project key={p.id} p={p} i={i} shareBase={shareBase} apiBase={apiBase} />)}
      </main>
      <footer className="border-t px-6 py-10 text-sm text-muted-foreground sm:px-12">
        <p className="flex flex-wrap items-center gap-4">{ws.name}<ElmMark /></p>
      </footer>
    </div>
  );
}
